import { createBrowserRouter, Navigate } from 'react-router-dom';

import AuthLayout from '@/app/(auth)/layout';
import LoginPage from '@/app/(auth)/login/page';
import LoginSsoCallbackPage from '@/app/(auth)/login/sso-callback/page';
import RegisterPage from '@/app/(auth)/register/page';
import RegisterSsoCallbackPage from '@/app/(auth)/register/sso-callback/page';

import DashboardLayout from '@/app/(dashboard)/layout';
import HomePage from '@/app/(dashboard)/home/page';
import BudgetPage from '@/app/(dashboard)/budget/page';
import ConnectionsPage from '@/app/(dashboard)/connections/page';
import ExpensesLayout from '@/app/(dashboard)/expenses/layout';
import ExpensesPage from '@/app/(dashboard)/expenses/page';
import ExpensesCreditPage from '@/app/(dashboard)/expenses/credit/page';
import ExpensesDebitPage from '@/app/(dashboard)/expenses/debit/page';
import FaturasPage from '@/app/(dashboard)/faturas/page';
import IncomePage from '@/app/(dashboard)/income/page';
import InvestmentsPage from '@/app/(dashboard)/investments/page';
import InvestmentsCalendarPage from '@/app/(dashboard)/investments/calendar/page';
import InvestmentDetailPage from '@/app/(dashboard)/investments/[id]/page';
import MetasPage from '@/app/(dashboard)/metas/page';
import MetasNewPage from '@/app/(dashboard)/metas/new/page';
import MetasEditPage from '@/app/(dashboard)/metas/[id]/edit/page';
import ProfilePage from '@/app/(dashboard)/profile/page';
import RecurringPaymentsPage from '@/app/(dashboard)/recurring-payments/page';
import RecurringPaymentsBreakdownPage from '@/app/(dashboard)/recurring-payments/breakdown/page';
import TransactionsPage from '@/app/(dashboard)/transactions/page';
import CategoryMerchantsPage from '@/app/(dashboard)/transactions/category/[categoryId]/merchants/page';
import OnboardingPage from '@/app/onboarding/page';
import { AuthAliasRedirect } from '@/components/AuthAliasRedirect';
import { LegacyCreditRedirect } from '@/components/LegacyCreditRedirect';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { SpacesRouteGate } from '@/components/spaces/SpacesRouteGate';
import { SpaceHomeRedirect, SpaceLayout, SpaceSheetRedirect, SpaceUpcomingRedirect } from '@/components/spaces/SpaceLayout';
import { SpaceExpensesPage } from '@/components/spaces/SpaceExpensesPage';
import { SpaceHomePage } from '@/components/spaces/SpaceHomePage';
import { SpaceSettingsPage } from '@/components/spaces/SpaceSettingsPage';
import { CreateSpacePage } from '@/components/spaces/CreateSpacePage';
import { SpaceRulesPage } from '@/components/spaces/SpaceRulesPage';
import { consumeReloadMarker } from '@/lib/chunk-reload';

// Must run before createBrowserRouter, which snapshots window.location on creation.
// Stripping the chunk-recovery marker afterwards would not reach that snapshot —
// history.replaceState fires no popstate — so useLocation() would keep serving `_cr`
// and AuthAliasRedirect would re-emit it into the address bar. Calling it here rather
// than in main.tsx makes the ordering a dependency-graph guarantee instead of a
// line-order convention that an import sorter could quietly undo.
consumeReloadMarker();

export const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/login/*', element: <LoginPage /> },
      { path: '/login/sso-callback', element: <LoginSsoCallbackPage /> },
      { path: '/register/*', element: <RegisterPage /> },
      { path: '/register/sso-callback', element: <RegisterSsoCallbackPage /> },
    ],
  },
  {
    element: <DashboardLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'relatorios', element: <Navigate to="/" replace /> },
      { path: 'budget', element: <BudgetPage /> },
      { path: 'connections', element: <ConnectionsPage /> },
      {
        path: 'expenses',
        element: <ExpensesLayout />,
        children: [
          { index: true, element: <ExpensesPage /> },
          { path: 'credit', element: <ExpensesCreditPage /> },
          { path: 'debit', element: <ExpensesDebitPage /> },
        ],
      },
      { path: 'invoices', element: <FaturasPage /> },
      { path: 'income', element: <IncomePage /> },
      { path: 'investments', element: <InvestmentsPage /> },
      { path: 'investments/calendar', element: <InvestmentsCalendarPage /> },
      { path: 'investments/:id', element: <InvestmentDetailPage /> },
      { path: 'metas', element: <MetasPage /> },
      { path: 'metas/new', element: <MetasNewPage /> },
      { path: 'metas/:id/edit', element: <MetasEditPage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'recurring-payments', element: <RecurringPaymentsPage /> },
      { path: 'recurring-payments/breakdown', element: <RecurringPaymentsBreakdownPage /> },
      { element: <SpacesRouteGate />, children: [{ path: 'spaces-rules', element: <SpaceRulesPage /> }] },
      // Espaços (VER-403): every /spaces/* route sits behind the shared-spaces flag — off,
      // it redirects to / exactly like the catch-all did before the feature existed.
      // Membership and model guards live inside the flag gate.
      {
        path: 'spaces',
        element: <SpacesRouteGate />,
        children: [
          { index: true, element: <Navigate to="/" replace /> },
          { path: 'new', element: <CreateSpacePage /> },
          {
            path: ':spaceId',
            element: <SpaceLayout />,
            children: [
              { index: true, element: <SpaceHomeRedirect /> },
              { path: 'home', element: <SpaceHomePage /> },
              { path: 'expenses', element: <SpaceExpensesPage /> },
              // Acerto and Categorias were pages until 2026-09-23; their addresses open the home's sheets now.
              { path: 'budget', element: <SpaceSheetRedirect sheet="budget" /> },
              { path: 'settle', element: <SpaceSheetRedirect sheet="settle" /> },
              { path: 'recurring', element: <SpaceUpcomingRedirect /> },
              { path: 'settings', element: <SpaceSettingsPage /> },
              { path: '*', element: <SpaceHomeRedirect /> },
            ],
          },
        ],
      },
      { path: 'transactions', element: <TransactionsPage /> },
      { path: 'transactions/category/:categoryId/merchants', element: <CategoryMerchantsPage /> },
      // Legacy redirects — keep for bookmarks/back-button compatibility.
      { path: 'transactions/debit', element: <Navigate to="/transactions" replace /> },
      { path: 'transactions/credit', element: <LegacyCreditRedirect /> },
      { path: 'transactions/income', element: <Navigate to="/transactions?type=income" replace /> },
      { path: 'transactions/recurring-payments', element: <Navigate to="/recurring-payments" replace /> },
      { path: 'faturas', element: <Navigate to="/invoices" replace /> },
    ],
  },
  // The five-step onboarding flow (VER-368/VER-388) — its own full-screen shell, no
  // dashboard chrome; the step position is resumable server-side.
  { path: '/onboarding', element: <OnboardingPage />, errorElement: <RouteErrorBoundary /> },
  // Clerk's conventional auth paths → this app's routes, keeping the query
  // string so an invitation's `__clerk_ticket` reaches <SignUp> at /register.
  { path: '/sign-up/*', element: <AuthAliasRedirect to="/register" /> },
  { path: '/sign-in/*', element: <AuthAliasRedirect to="/login" /> },
  { path: '*', element: <Navigate to="/" replace /> },
]);
